// Moves the user to a field that failed validation: scrolls it into view and
// puts the cursor in it. Works for plain inputs, react-select (the id is on
// the container), flatpickr (the visible input is the "alt" input) and the
// rich-text editors (contenteditable).

const FOCUSABLE =
  'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [contenteditable="true"], button:not([disabled])';

const resolveTarget = element => {
  if (!element) return null;
  if (element._flatpickr) {
    return element._flatpickr.altInput || element._flatpickr.input || element;
  }
  if (element.matches(FOCUSABLE)) return element;
  return element.querySelector(FOCUSABLE) || element;
};

const isRendered = element =>
  Boolean(element) && element.getClientRects().length > 0;

// Visually hidden inputs (card radios, custom switches) are reached through
// their label, which is what the user sees.
const visibleAnchor = element => {
  if (!element) return null;
  const altInput = element._flatpickr?.altInput;
  if (isRendered(altInput)) return altInput;
  if (isRendered(element)) return element;
  const label = element?.id
    ? document.querySelector(`label[for="${CSS.escape(element.id)}"]`)
    : null;
  return isRendered(label) ? label : null;
};

export const focusFieldById = id => {
  if (!id) return false;
  const element = document.getElementById(id);
  // A field inside a collapsed card or a closed tab has no layout yet.
  const anchor = visibleAnchor(element);
  if (!anchor) return false;
  const target = anchor.tagName === 'LABEL' ? element : resolveTarget(element);
  anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
  if (typeof target.focus === 'function') target.focus({ preventScroll: true });
  return true;
};

// Focuses whichever of the given ids comes first on the page.
export const focusFirstField = ids => {
  const anchors = ids
    .map(id => ({ id, anchor: visibleAnchor(document.getElementById(id)) }))
    .filter(item => item.anchor);
  if (anchors.length === 0) return false;
  anchors.sort((a, b) =>
    a.anchor.compareDocumentPosition(b.anchor) &
    Node.DOCUMENT_POSITION_FOLLOWING
      ? -1
      : 1
  );
  return focusFieldById(anchors[0].id);
};

// Requirement validation error keys -> id of the field that shows them.
// Clause errors are keyed by the clause id, which is also the editor id.
const REQUIREMENT_FIELD_IDS = {
  contractType: 'contractType01',
};

export const requirementFieldId = errorKey =>
  REQUIREMENT_FIELD_IDS[errorKey] || errorKey;
